/**
 * FormatTemplate v2 — canonical print layout config.
 * Migrates from legacy PublishSettings (v1 / unversioned).
 */

import type {
  BodyFont,
  CaptionAlign,
  CaptionPlacement,
  ChapterDecoration,
  ChapterDecorationKind,
  ChapterDecorationLineStyle,
  ChapterHeadingStyle,
  ChapterNumberFormat,
  ChapterStartPage,
  HeaderContent,
  HeadingAlign,
  PageNumberPlacement,
  PublishFont,
  PublishPlatform,
  PublishSettings,
  TocStyle,
  TrimSize,
} from "./publishFormattingShared";
import {
  BODY_FONTS,
  defaultChapterDecoration,
  defaultPublishSettings,
  PUBLISH_FONTS,
} from "./publishFormattingShared";
import type { BookContentKind } from "./bookSpine";

export const FORMAT_TEMPLATE_VERSION = 2 as const;

/** Per-side header/footer slot content. */
export type HeaderFooterSlot =
  | "none"
  | "book-title"
  | "author-name"
  | "chapter-title"
  | "page-number"
  | "alternating-title-author";

export type ChapterTitlePlacement = "center" | "left" | "drop-cap-adjacent";

function isPublishFont(v: unknown): v is PublishFont {
  return typeof v === "string" && (PUBLISH_FONTS as string[]).includes(v);
}

function isBodyFont(v: unknown): v is BodyFont {
  return typeof v === "string" && (BODY_FONTS as string[]).includes(v);
}

function parsePublishFontOrSame(v: unknown, fallback: PublishFont | "same"): PublishFont | "same" {
  if (v === "same") return "same";
  if (isPublishFont(v)) return v;
  return fallback;
}

export type KindTypesetStyle = {
  /** Start this kind on a recto (right-hand) page when possible. */
  startOnRecto?: boolean;
  /** Suppress running header/footer on the first page of this item. */
  suppressHeaderFooterOnFirstPage?: boolean;
  /** Optional ornament drawn above the title. */
  ornament?: string;
  titlePlacement?: ChapterTitlePlacement;
  dropCap?: boolean;
};

export type FormatTemplate = {
  version: typeof FORMAT_TEMPLATE_VERSION;
  name: string;
  platform: PublishPlatform;
  trimSize: TrimSize;
  /** Margins in inches. Inner/outer swap by verso/recto automatically. */
  margins: { top: number; bottom: number; inner: number; outer: number };
  typography: PublishSettings["typography"];
  chapterSettings: PublishSettings["chapterSettings"] & {
    titlePlacement?: ChapterTitlePlacement;
  };
  toc: PublishSettings["toc"];
  headerFooter: PublishSettings["headerFooter"] & {
    /** Optional explicit verso/recto slots (override headerContent when set). */
    versoHeader?: HeaderFooterSlot;
    rectoHeader?: HeaderFooterSlot;
    versoFooter?: HeaderFooterSlot;
    rectoFooter?: HeaderFooterSlot;
  };
  /** Legacy boolean front/back matter — used when spine has no matter items. */
  frontMatter: PublishSettings["frontMatter"];
  backMatter: PublishSettings["backMatter"];
  sectionBreakOrnament: string;
  /** How each spine kind is typeset. */
  kindStyles: Partial<Record<BookContentKind, KindTypesetStyle>>;
  /** Reserved for title-page / chapter images (paths or data URLs). */
  titleImage?: string | null;
  contentImages?: Array<{ id: string; src: string; caption?: string }>;
};

export function defaultKindStyles(): FormatTemplate["kindStyles"] {
  return {
    chapter: {
      startOnRecto: true,
      suppressHeaderFooterOnFirstPage: true,
      titlePlacement: "center",
      dropCap: false,
    },
    foreword: { startOnRecto: true, suppressHeaderFooterOnFirstPage: true },
    prologue: { startOnRecto: true, suppressHeaderFooterOnFirstPage: true },
    dedication: { startOnRecto: true, suppressHeaderFooterOnFirstPage: true },
    copyright: { startOnRecto: false, suppressHeaderFooterOnFirstPage: true },
    about_author: { startOnRecto: true, suppressHeaderFooterOnFirstPage: true },
    also_by: { startOnRecto: true, suppressHeaderFooterOnFirstPage: true },
    appendix: { startOnRecto: true, suppressHeaderFooterOnFirstPage: true },
    bibliography: { startOnRecto: true, suppressHeaderFooterOnFirstPage: true },
  };
}

export function defaultFormatTemplate(name = "Default"): FormatTemplate {
  const base = defaultPublishSettings();
  return {
    version: FORMAT_TEMPLATE_VERSION,
    name,
    platform: base.platform,
    trimSize: base.trimSize,
    margins: { ...base.margins },
    typography: { ...base.typography },
    chapterSettings: {
      ...base.chapterSettings,
      titlePlacement: "center",
    },
    toc: { ...base.toc },
    headerFooter: { ...base.headerFooter },
    frontMatter: { ...base.frontMatter },
    backMatter: { ...base.backMatter },
    sectionBreakOrnament: base.sectionBreakOrnament,
    kindStyles: defaultKindStyles(),
    titleImage: null,
    contentImages: [],
  };
}

function asRecord(raw: unknown): Record<string, unknown> | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  return raw as Record<string, unknown>;
}

/** Upgrade legacy PublishSettings / partial JSON into FormatTemplate v2. */
export function migrateTemplate(raw: unknown): FormatTemplate {
  const defaults = defaultFormatTemplate();
  const rec = asRecord(raw);
  if (!rec) return defaults;

  const version = typeof rec.version === "number" ? rec.version : 1;

  const knownTrim = new Set([
    "5x8",
    "5.5x8.5",
    "6x9",
    "8x10",
    "8.5x11",
    "A5",
    "A4",
  ]);
  const platform =
    rec.platform === "kdp-print" || rec.platform === "ingramspark" || rec.platform === "custom"
      ? rec.platform
      : defaults.platform;

  const trimSize =
    typeof rec.trimSize === "string" && knownTrim.has(rec.trimSize)
      ? (rec.trimSize as TrimSize)
      : defaults.trimSize;

  const marginsRec = asRecord(rec.margins);
  const margins = {
    top: typeof marginsRec?.top === "number" ? marginsRec.top : defaults.margins.top,
    bottom: typeof marginsRec?.bottom === "number" ? marginsRec.bottom : defaults.margins.bottom,
    inner: typeof marginsRec?.inner === "number" ? marginsRec.inner : defaults.margins.inner,
    outer: typeof marginsRec?.outer === "number" ? marginsRec.outer : defaults.margins.outer,
  };

  const typoRec = asRecord(rec.typography);
  const typography = {
    ...defaults.typography,
    ...(typoRec
      ? {
          bodyFont: isBodyFont(typoRec.bodyFont)
            ? typoRec.bodyFont
            : defaults.typography.bodyFont,
          bodyFontSize:
            typeof typoRec.bodyFontSize === "number"
              ? typoRec.bodyFontSize
              : defaults.typography.bodyFontSize,
          lineSpacing:
            typeof typoRec.lineSpacing === "number"
              ? typoRec.lineSpacing
              : defaults.typography.lineSpacing,
          paragraphStyle:
            typoRec.paragraphStyle === "spacing" || typoRec.paragraphStyle === "indent"
              ? typoRec.paragraphStyle
              : defaults.typography.paragraphStyle,
          firstLineIndent:
            typeof typoRec.firstLineIndent === "number"
              ? typoRec.firstLineIndent
              : defaults.typography.firstLineIndent,
          paragraphSpacing:
            typeof typoRec.paragraphSpacing === "number"
              ? Math.max(0, typoRec.paragraphSpacing)
              : defaults.typography.paragraphSpacing,
        }
      : {}),
  };

  const chRec = asRecord(rec.chapterSettings);
  const rawHeadingStyle = typeof chRec?.headingStyle === "string" ? chRec.headingStyle : null;
  const wasDecorative = rawHeadingStyle === "decorative";
  const headingStyle: ChapterHeadingStyle =
    rawHeadingStyle === "title-only" ||
    rawHeadingStyle === "number-only" ||
    rawHeadingStyle === "number-and-title"
      ? rawHeadingStyle
      : wasDecorative
        ? "number-and-title"
        : defaults.chapterSettings.headingStyle;

  const sectionBreakOrnament =
    typeof rec.sectionBreakOrnament === "string"
      ? rec.sectionBreakOrnament
      : defaults.sectionBreakOrnament;

  const decRec = asRecord(chRec?.decoration);
  const defaultDec = defaultChapterDecoration();
  const decorationKind: ChapterDecorationKind =
    decRec?.kind === "text" ||
    decRec?.kind === "ornament" ||
    decRec?.kind === "image" ||
    decRec?.kind === "line"
      ? decRec.kind
      : defaultDec.kind;
  const lineStyle: ChapterDecorationLineStyle =
    decRec?.lineStyle === "thin" ||
    decRec?.lineStyle === "medium" ||
    decRec?.lineStyle === "thick" ||
    decRec?.lineStyle === "double"
      ? decRec.lineStyle
      : defaultDec.lineStyle;
  const decoration: ChapterDecoration = {
    kind: decorationKind,
    text:
      typeof decRec?.text === "string"
        ? decRec.text
        : wasDecorative
          ? sectionBreakOrnament || defaultDec.text
          : defaultDec.text,
    ornament: typeof decRec?.ornament === "string" ? decRec.ornament : defaultDec.ornament,
    imageDataUrl:
      typeof decRec?.imageDataUrl === "string" && decRec.imageDataUrl.startsWith("data:")
        ? decRec.imageDataUrl
        : null,
    imageAspect:
      typeof decRec?.imageAspect === "number" && decRec.imageAspect > 0
        ? decRec.imageAspect
        : null,
    maxHeight:
      typeof decRec?.maxHeight === "number" && decRec.maxHeight > 0
        ? decRec.maxHeight
        : defaultDec.maxHeight,
    lineStyle,
  };

  const legacyHeadingFontSize =
    typeof chRec?.headingFontSize === "number"
      ? chRec.headingFontSize
      : defaults.chapterSettings.headingFontSize;
  const numberFontSize =
    typeof chRec?.numberFontSize === "number"
      ? Math.max(8, chRec.numberFontSize)
      : legacyHeadingFontSize;
  const titleFontSize =
    typeof chRec?.titleFontSize === "number"
      ? Math.max(8, chRec.titleFontSize)
      : Math.max(8, Math.round(legacyHeadingFontSize * 0.7));

  const titlePlacement =
    chRec?.titlePlacement === "left" ||
    chRec?.titlePlacement === "center" ||
    chRec?.titlePlacement === "drop-cap-adjacent"
      ? chRec.titlePlacement
      : defaults.chapterSettings.titlePlacement;

  let headingAlign: HeadingAlign = defaults.chapterSettings.headingAlign;
  if (chRec?.headingAlign === "left" || chRec?.headingAlign === "center" || chRec?.headingAlign === "right") {
    headingAlign = chRec.headingAlign;
  } else if (titlePlacement === "left") {
    headingAlign = "left";
  } else if (titlePlacement === "center") {
    headingAlign = "center";
  }

  const chapterSettings = {
    ...defaults.chapterSettings,
    ...(chRec
      ? {
          headingStyle,
          numberFormat: (chRec.numberFormat as ChapterNumberFormat) || defaults.chapterSettings.numberFormat,
          chapterStartPage:
            (chRec.chapterStartPage as ChapterStartPage) || defaults.chapterSettings.chapterStartPage,
          dropCap: typeof chRec.dropCap === "boolean" ? chRec.dropCap : defaults.chapterSettings.dropCap,
          headingFont: parsePublishFontOrSame(chRec.headingFont, defaults.chapterSettings.headingFont),
          decorationFont: parsePublishFontOrSame(
            chRec.decorationFont,
            defaults.chapterSettings.decorationFont
          ),
          headingAlign,
          headingFontSize: legacyHeadingFontSize,
          numberFontSize,
          titleFontSize,
          spaceBefore:
            typeof chRec.spaceBefore === "number"
              ? chRec.spaceBefore
              : defaults.chapterSettings.spaceBefore,
          spaceAfter:
            typeof chRec.spaceAfter === "number"
              ? Math.max(0, chRec.spaceAfter)
              : defaults.chapterSettings.spaceAfter,
          showDecoration:
            typeof chRec.showDecoration === "boolean"
              ? chRec.showDecoration
              : wasDecorative
                ? true
                : defaults.chapterSettings.showDecoration,
          decoration,
          titlePlacement,
          showCaption:
            typeof chRec.showCaption === "boolean"
              ? chRec.showCaption
              : defaults.chapterSettings.showCaption,
          captionPlacement:
            chRec.captionPlacement === "above-title" || chRec.captionPlacement === "below-title"
              ? (chRec.captionPlacement as CaptionPlacement)
              : defaults.chapterSettings.captionPlacement,
          captionAlign:
            chRec.captionAlign === "left" || chRec.captionAlign === "center"
              ? (chRec.captionAlign as CaptionAlign)
              : defaults.chapterSettings.captionAlign,
          captionItalic:
            typeof chRec.captionItalic === "boolean"
              ? chRec.captionItalic
              : defaults.chapterSettings.captionItalic,
          captionFontSize:
            typeof chRec.captionFontSize === "number"
              ? Math.max(8, chRec.captionFontSize)
              : defaults.chapterSettings.captionFontSize,
          captionSpaceBefore:
            typeof chRec.captionSpaceBefore === "number"
              ? Math.max(0, chRec.captionSpaceBefore)
              : defaults.chapterSettings.captionSpaceBefore,
          captionSpaceAfter:
            typeof chRec.captionSpaceAfter === "number"
              ? Math.max(0, chRec.captionSpaceAfter)
              : defaults.chapterSettings.captionSpaceAfter,
        }
      : {}),
  };

  const tocRec = asRecord(rec.toc);
  const toc = {
    ...defaults.toc,
    ...(tocRec
      ? {
          include: typeof tocRec.include === "boolean" ? tocRec.include : defaults.toc.include,
          title: typeof tocRec.title === "string" ? tocRec.title : defaults.toc.title,
          style: (tocRec.style as TocStyle) || defaults.toc.style,
        }
      : {}),
  };

  const hfRec = asRecord(rec.headerFooter);
  const headerFooter = {
    ...defaults.headerFooter,
    ...(hfRec
      ? {
          headerContent: (hfRec.headerContent as HeaderContent) || defaults.headerFooter.headerContent,
          pageNumbers: (hfRec.pageNumbers as PageNumberPlacement) || defaults.headerFooter.pageNumbers,
          suppressOnChapterFirstPage:
            typeof hfRec.suppressOnChapterFirstPage === "boolean"
              ? hfRec.suppressOnChapterFirstPage
              : defaults.headerFooter.suppressOnChapterFirstPage,
          versoHeader: hfRec.versoHeader as HeaderFooterSlot | undefined,
          rectoHeader: hfRec.rectoHeader as HeaderFooterSlot | undefined,
          versoFooter: hfRec.versoFooter as HeaderFooterSlot | undefined,
          rectoFooter: hfRec.rectoFooter as HeaderFooterSlot | undefined,
        }
      : {}),
  };

  const fmRec = asRecord(rec.frontMatter);
  const frontMatter = {
    ...defaults.frontMatter,
    ...(fmRec ? { ...defaults.frontMatter, ...fmRec } : {}),
  } as FormatTemplate["frontMatter"];

  const bmRec = asRecord(rec.backMatter);
  const backMatter = {
    ...defaults.backMatter,
    ...(bmRec ? { ...defaults.backMatter, ...bmRec } : {}),
  } as FormatTemplate["backMatter"];

  const kindStyles =
    version >= 2 && asRecord(rec.kindStyles)
      ? { ...defaultKindStyles(), ...(rec.kindStyles as FormatTemplate["kindStyles"]) }
      : defaultKindStyles();

  // Sync chapter start / dropCap into kindStyles.chapter from chapterSettings
  if (kindStyles.chapter) {
    kindStyles.chapter = {
      ...kindStyles.chapter,
      startOnRecto: chapterSettings.chapterStartPage === "recto",
      suppressHeaderFooterOnFirstPage: headerFooter.suppressOnChapterFirstPage,
      dropCap: chapterSettings.dropCap,
      titlePlacement: chapterSettings.titlePlacement,
    };
  }

  return {
    version: FORMAT_TEMPLATE_VERSION,
    name: typeof rec.name === "string" && rec.name.trim() ? rec.name.trim() : defaults.name,
    platform,
    trimSize: (trimSize as TrimSize) || defaults.trimSize,
    margins,
    typography,
    chapterSettings,
    toc,
    headerFooter,
    frontMatter,
    backMatter,
    sectionBreakOrnament,
    kindStyles,
    titleImage: typeof rec.titleImage === "string" ? rec.titleImage : null,
    contentImages: Array.isArray(rec.contentImages)
      ? (rec.contentImages as FormatTemplate["contentImages"])
      : [],
  };
}

/** Project FormatTemplate back to PublishSettings for legacy UI bindings. */
export function formatTemplateToPublishSettings(template: FormatTemplate): PublishSettings {
  return {
    platform: template.platform,
    trimSize: template.trimSize,
    margins: template.margins,
    typography: template.typography,
    chapterSettings: {
      headingStyle: template.chapterSettings.headingStyle,
      numberFormat: template.chapterSettings.numberFormat,
      chapterStartPage: template.chapterSettings.chapterStartPage,
      dropCap: template.chapterSettings.dropCap,
      headingFont: template.chapterSettings.headingFont,
      decorationFont: template.chapterSettings.decorationFont,
      headingAlign: template.chapterSettings.headingAlign,
      headingFontSize: template.chapterSettings.headingFontSize,
      numberFontSize: template.chapterSettings.numberFontSize,
      titleFontSize: template.chapterSettings.titleFontSize,
      spaceBefore: template.chapterSettings.spaceBefore,
      spaceAfter: template.chapterSettings.spaceAfter,
      showDecoration: template.chapterSettings.showDecoration,
      decoration: { ...template.chapterSettings.decoration },
      showCaption: template.chapterSettings.showCaption,
      captionPlacement: template.chapterSettings.captionPlacement,
      captionAlign: template.chapterSettings.captionAlign,
      captionItalic: template.chapterSettings.captionItalic,
      captionFontSize: template.chapterSettings.captionFontSize,
      captionSpaceBefore: template.chapterSettings.captionSpaceBefore,
      captionSpaceAfter: template.chapterSettings.captionSpaceAfter,
    },
    toc: template.toc,
    headerFooter: {
      headerContent: template.headerFooter.headerContent,
      pageNumbers: template.headerFooter.pageNumbers,
      suppressOnChapterFirstPage: template.headerFooter.suppressOnChapterFirstPage,
    },
    frontMatter: template.frontMatter,
    backMatter: template.backMatter,
    sectionBreakOrnament: template.sectionBreakOrnament,
  };
}
